import type {
  AuthRequest,
  AuthResponseDto,
  TelegramLinkDto,
  TelegramStatusDto,
  AvailabilityDto,
  CreateReservationRequest,
  PagedResponseDto,
  ReservationDto,
  RestaurantDto,
  ReviewDto,
  UpdateReservationRequest,
  UserDto,
} from '../dto';

/**
 * Respuesta cruda de la capa de red: el cuerpo y el código de estado, sin interpretar.
 *
 * Se devuelve en lugar de lanzar para que el repositorio pueda clasificar el código con
 * `ErrorMapper` en vez de depender de excepciones, igual que el `Response<T>` de Retrofit en el
 * cliente Android.
 */
export interface ApiResponse<T> {
  readonly status: number;
  readonly body: T | null;
  /** Campos de un 422, para pintar el error debajo de cada input. */
  readonly fields?: Record<string, string>;
}

export function apiOk<T>(body: T): ApiResponse<T> {
  return { status: 200, body };
}

export function apiError<T>(status: number, fields?: Record<string, string>): ApiResponse<T> {
  return { status, body: null, fields };
}

export function isSuccessful<T>(response: ApiResponse<T>): boolean {
  return response.status >= 200 && response.status < 300;
}

export interface SearchQuery {
  query?: string | null;
  locationId?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  cuisines?: readonly string[] | null;
  priceLevels?: readonly number[] | null;
  minRating?: number | null;
  maxDistanceMeters?: number | null;
  amenities?: readonly string[] | null;
  onlyAvailable?: boolean;
  date?: string | null;
  time?: string | null;
  partySize?: number | null;
  sort?: string | null;
  page?: number;
  size?: number;
}

export interface RestaurantsQuery {
  latitude?: number | null;
  longitude?: number | null;
  locationId?: string | null;
  collection?: 'recommended' | 'nearby' | 'featured' | null;
  page?: number;
  size?: number;
}

/**
 * Contrato HTTP propio (docs/ANALISIS.md §9). **No** reproduce ninguna API de OpenTable.
 *
 * `FakeApi` y `HttpApi` implementan esta misma interfaz, así que pasar al backend real solo
 * cambia qué implementación construye `createApi()`; ni los repositorios ni las pantallas
 * cambian una línea.
 */
export interface ApiService {
  getRestaurants(query?: RestaurantsQuery): Promise<ApiResponse<PagedResponseDto<RestaurantDto>>>;

  getRestaurant(id: string): Promise<ApiResponse<RestaurantDto>>;

  getReviews(id: string, page?: number, size?: number): Promise<ApiResponse<PagedResponseDto<ReviewDto>>>;

  searchRestaurants(query: SearchQuery): Promise<ApiResponse<PagedResponseDto<RestaurantDto>>>;

  getAvailability(
    id: string,
    date: string,
    time: string,
    partySize: number,
  ): Promise<ApiResponse<AvailabilityDto>>;

  createReservation(
    idempotencyKey: string,
    body: CreateReservationRequest,
  ): Promise<ApiResponse<ReservationDto>>;

  getReservations(status?: string | null): Promise<ApiResponse<ReservationDto[]>>;

  getReservation(id: string): Promise<ApiResponse<ReservationDto>>;

  updateReservation(id: string, body: UpdateReservationRequest): Promise<ApiResponse<ReservationDto>>;

  cancelReservation(id: string): Promise<ApiResponse<void>>;

  /**
   * Abre una verificación por Telegram y devuelve el enlace del bot.
   *
   * Solo se manda el nombre: el teléfono lo aporta Telegram cuando la persona comparte su
   * contacto, que es lo que hace que esto verifique de verdad y no sea un campo más.
   */
  startTelegramLink(name: string, returnUrl?: string): Promise<ApiResponse<TelegramLinkDto>>;

  /** Estado de la verificación. Cuando responde `VERIFIED`, trae la sesión. */
  telegramLinkStatus(token: string): Promise<ApiResponse<TelegramStatusDto>>;

  /**
   * Alta escribiendo el teléfono, sin verificar.
   *
   * Solo la acepta un servidor sin bot configurado. Existe para no dejar la aplicación sin
   * entrada mientras se termina de montar el bot; con el bot activo responde 403.
   */
  identify(body: AuthRequest): Promise<ApiResponse<AuthResponseDto>>;

  getProfile(): Promise<ApiResponse<UserDto>>;

  updateProfile(body: UserDto): Promise<ApiResponse<UserDto>>;
}
